import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  DeleteOutlined,
  DownloadOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  AimOutlined,
  EnvironmentOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'

import { get, post, del } from '@/utils/request'
import { useUserStore } from '@/store/useDeviceStore'
import { loadAMap } from '@/utils/loadAMap'

const { RangePicker } = DatePicker
const { Text } = Typography

const API_BASE = import.meta.env.VITE_API_BASE || ''

interface DeviceItem {
  dev_id: string
  name?: string
}

interface VideoItem {
  id: number
  dev_id: string
  source_type: string
  file_name: string
  relative_path: string
  file_size: number
  started_at?: string
  ended_at?: string
  file_created_at?: string
  file_modified_at?: string
  exists: boolean
}

interface TrackPoint {
  lon: number
  lat: number
  ts: number
}

function formatTime(value?: string) {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-'
}

function formatSize(size: number) {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`
  return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function VideoManagement() {
  const { token, isAdmin } = useUserStore()
  const [devices, setDevices] = useState<DeviceItem[]>([])
  const [videos, setVideos] = useState<VideoItem[]>([])
  const [loading, setLoading] = useState(false)
  const [rescanLoading, setRescanLoading] = useState(false)
  const [deviceFilter, setDeviceFilter] = useState<string | undefined>(undefined)
  const [keyword, setKeyword] = useState('')
  const [timeRange, setTimeRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [playerOpen, setPlayerOpen] = useState(false)
  const [playerLoading, setPlayerLoading] = useState(false)
  const [playerError, setPlayerError] = useState<string | null>(null)
  const [playerUrl, setPlayerUrl] = useState('')
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null)
  const [trackMapOpen, setTrackMapOpen] = useState(false)
  const [trackMapLoading, setTrackMapLoading] = useState(false)
  const [trackMapError, setTrackMapError] = useState<string | null>(null)
  const [activeTrack, setActiveTrack] = useState<{ record: VideoItem; points: TrackPoint[] } | null>(null)
  const trackMapContainerRef = useRef<HTMLDivElement>(null)
  const trackMapRef = useRef<AMap.Map | null>(null)

  const adminUser = isAdmin()

  const loadDevices = async () => {
    try {
      const result = await get<{ code: number; data: DeviceItem[] }>('/api/v1/devices/list', { showError: false })
      if (result.code === 0) {
        setDevices(result.data || [])
      }
    } catch (error) {
      console.error('获取设备列表失败', error)
    }
  }

  const loadVideos = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    currentDeviceFilter = deviceFilter,
    currentKeyword = keyword,
    currentTimeRange = timeRange
  ) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      })
      if (currentDeviceFilter) {
        params.set('dev_id', currentDeviceFilter)
      }
      if (currentKeyword.trim()) {
        params.set('keyword', currentKeyword.trim())
      }
      if (currentTimeRange) {
        params.set('start_ts', String(currentTimeRange[0].valueOf()))
        params.set('end_ts', String(currentTimeRange[1].valueOf()))
      }

      const result = await get<{ total: number; items: VideoItem[] }>(`/api/v1/videos?${params.toString()}`)
      setVideos(result.items)
      setPagination({ current: page, pageSize, total: result.total })
    } catch (error) {
      // 错误已在请求封装处理
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDevices()
    loadVideos()
  }, [])

  const handleSearch = () => {
    loadVideos(1, pagination.pageSize)
  }

  const handleReset = () => {
    setDeviceFilter(undefined)
    setKeyword('')
    setTimeRange(null)
    loadVideos(1, pagination.pageSize, undefined, '', null)
  }

  const handleRescan = async () => {
    setRescanLoading(true)
    try {
      await post('/api/v1/videos/rescan', { dev_id: deviceFilter || null })
      message.success('录像目录重扫完成')
      loadVideos(1, pagination.pageSize)
    } catch (error) {
      // 错误已在请求封装处理
    } finally {
      setRescanLoading(false)
    }
  }

  const handleDelete = async (videoId: number) => {
    try {
      await del(`/api/v1/videos/${videoId}`)
      message.success('录像文件已删除')
      loadVideos()
    } catch (error) {
      // 错误已在请求封装处理
    }
  }

  const handleDownload = async (record: VideoItem) => {
    if (!token) {
      message.error('登录状态无效')
      return
    }

    try {
      const response = await fetch(`${API_BASE}/api/v1/videos/${record.id}/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.detail || '下载失败')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = record.file_name
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      message.error(error instanceof Error ? error.message : '下载失败')
    }
  }

  const handleTargetTrackDownload = async (record: VideoItem) => {
    if (!token) {
      message.error('登录状态无效')
      return
    }

    try {
      const response = await fetch(`${API_BASE}/api/v1/videos/${record.id}/target-track/download`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.detail || '目标轨迹下载失败')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const fileName = response.headers.get('Content-Disposition')?.match(/filename\*=UTF-8''(.+)/)?.[1]
      link.download = fileName ? decodeURIComponent(fileName) : '目标轨迹.txt'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      message.error(error instanceof Error ? error.message : '目标轨迹下载失败')
    }
  }

  const handleViewTrack = async (record: VideoItem) => {
    setTrackMapLoading(true)
    setTrackMapError(null)
    try {
      const result = await get<{ code: number; data: { dev_id: string; points: TrackPoint[] } }>(
        `/api/v1/videos/${record.id}/target-track`
      )
      if (!result.data.points.length) {
        setTrackMapError('该录像时间段内没有可用的目标轨迹坐标')
      }
      setActiveTrack({ record, points: result.data.points })
      setTrackMapOpen(true)
    } catch (error) {
      setTrackMapError(error instanceof Error ? error.message : '目标轨迹查询失败')
      setTrackMapOpen(true)
    } finally {
      setTrackMapLoading(false)
    }
  }

  useEffect(() => {
    if (!trackMapOpen || !activeTrack?.points.length || !trackMapContainerRef.current) return

    let cancelled = false
    loadAMap()
      .then((AMapInstance) => {
        if (cancelled || !trackMapContainerRef.current) return
        const path = activeTrack.points.map((point) => [point.lon, point.lat] as [number, number])
        const map = new AMapInstance.Map(trackMapContainerRef.current, {
          center: path[0],
          zoom: 15,
          viewMode: '2D',
          resizeEnable: true,
        })
        const polyline = new AMapInstance.Polyline({
          path,
          strokeColor: '#f5222d',
          strokeWeight: 4,
          strokeOpacity: 0.9,
          lineJoin: 'round',
          lineCap: 'round',
        })
        map.add(polyline)
        map.setFitView([polyline], false, [48, 48, 48, 48])
        trackMapRef.current = map
      })
      .catch((error) => {
        if (!cancelled) setTrackMapError(error.message || '高德地图加载失败')
      })

    return () => {
      cancelled = true
      trackMapRef.current?.destroy()
      trackMapRef.current = null
    }
  }, [trackMapOpen, activeTrack])

  const handlePlay = (record: VideoItem) => {
    if (!token) {
      message.error('登录状态无效')
      return
    }

    setActiveVideo(record)
    setPlayerError(null)
    setPlayerLoading(true)
    setPlayerUrl(`${API_BASE}/api/v1/videos/${record.id}/stream?token=${encodeURIComponent(token)}`)
    setPlayerOpen(true)
  }

  const handleClosePlayer = () => {
    setPlayerOpen(false)
    setPlayerLoading(false)
    setPlayerError(null)
    setPlayerUrl('')
    setActiveVideo(null)
  }

  const columns = [
    {
      title: '设备',
      dataIndex: 'dev_id',
      key: 'dev_id',
    },
    {
      title: '文件名',
      dataIndex: 'file_name',
      key: 'file_name',
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: '视频源',
      dataIndex: 'source_type',
      key: 'source_type',
      render: (value: string) => (
        <Tag color={value === 'ir' ? 'volcano' : value === 'vis' ? 'blue' : 'default'}>
          {value === 'ir' ? '红外' : value === 'vis' ? '可见光' : '未知'}
        </Tag>
      ),
    },
    {
      title: '开始时间',
      dataIndex: 'started_at',
      key: 'started_at',
      render: (value: string) => formatTime(value),
    },
    {
      title: '结束时间',
      dataIndex: 'ended_at',
      key: 'ended_at',
      render: (value: string) => formatTime(value),
    },
    {
      title: '大小',
      dataIndex: 'file_size',
      key: 'file_size',
      render: (value: number) => formatSize(value),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, record: VideoItem) => (
        <Space size={0}>
          <Button
            type="link"
            icon={<PlayCircleOutlined />}
            disabled={!record.exists}
            onClick={() => handlePlay(record)}
          >
            播放
          </Button>
          <Button
            type="link"
            icon={<DownloadOutlined />}
            disabled={!record.exists}
            onClick={() => handleDownload(record)}
          >
            下载
          </Button>
          <Button
            type="link"
            icon={<AimOutlined />}
            onClick={() => handleTargetTrackDownload(record)}
          >
            目标轨迹下载
          </Button>
          <Button
            type="link"
            icon={<EnvironmentOutlined />}
            onClick={() => handleViewTrack(record)}
          >
            查看轨迹
          </Button>
          {adminUser && (
            <Popconfirm
              title="确定删除该录像文件吗？"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div className="page-shell" style={{ overflow: 'hidden' }}>
      <Card
        title={
          <Space>
            <VideoCameraOutlined />
            <span>视频管理</span>
          </Space>
        }
        style={{ flex: '1 1 auto', minHeight: 0 }}
        styles={{
          body: {
            height: 'calc(100% - 57px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <div style={{ flex: '0 0 auto' }}>
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              allowClear
              value={deviceFilter}
              onChange={setDeviceFilter}
              placeholder="按设备筛选"
              style={{ width: 180 }}
              options={devices.map((device) => ({
                value: device.dev_id,
                label: device.name || device.dev_id,
              }))}
            />
            <RangePicker
              showTime
              value={timeRange}
              onChange={(dates) => setTimeRange(dates as [Dayjs, Dayjs] | null)}
            />
            <Input
              allowClear
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索文件名"
              style={{ width: 220 }}
              onPressEnter={handleSearch}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              查询
            </Button>
            <Button onClick={handleReset}>重置</Button>
            {adminUser && (
              <Button icon={<ReloadOutlined />} loading={rescanLoading} onClick={handleRescan}>
                刷新
              </Button>
            )}
          </Space>
        </div>

        <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
          <Table
            rowKey="id"
            dataSource={videos}
            columns={columns}
            loading={loading}
            tableLayout="auto"
            pagination={false}
          />
        </div>
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            justifyContent: 'flex-end',
            paddingTop: 12,
            marginTop: 12,
            borderTop: '1px solid #f0f0f0',
            background: '#fff',
          }}
        >
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            onChange={(page, pageSize) => loadVideos(page, pageSize)}
          />
        </div>
      </Card>

      <Modal
        open={playerOpen}
        title="录像播放"
        footer={null}
        width={960}
        destroyOnClose
        onCancel={handleClosePlayer}
      >
        {activeVideo && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="文件名" span={2}>
                {activeVideo.file_name}
              </Descriptions.Item>
              <Descriptions.Item label="设备">{activeVideo.dev_id}</Descriptions.Item>
              <Descriptions.Item label="视频源">
                {activeVideo.source_type === 'ir'
                  ? '红外'
                  : activeVideo.source_type === 'vis'
                    ? '可见光'
                    : '未知'}
              </Descriptions.Item>
              <Descriptions.Item label="开始时间">{formatTime(activeVideo.started_at)}</Descriptions.Item>
              <Descriptions.Item label="结束时间">{formatTime(activeVideo.ended_at)}</Descriptions.Item>
            </Descriptions>

            {playerError && (
              <Alert
                showIcon
                type="error"
                message={playerError}
              />
            )}

            <Spin spinning={playerLoading} tip="正在加载录像...">
              <div
                style={{
                  background: '#000',
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <video
                  key={playerUrl}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  src={playerUrl}
                  style={{
                    width: '100%',
                    maxHeight: '70vh',
                    display: 'block',
                    background: '#000',
                  }}
                  onCanPlay={() => setPlayerLoading(false)}
                  onLoadedMetadata={() => setPlayerLoading(false)}
                  onError={() => {
                    setPlayerLoading(false)
                    setPlayerError('录像播放失败，请检查文件是否完整，或重新登录后重试')
                  }}
                />
              </div>
            </Spin>
          </Space>
        )}
      </Modal>

      <Modal
        open={trackMapOpen}
        title={activeTrack ? `${activeTrack.record.dev_id} 目标轨迹` : '目标轨迹'}
        footer={null}
        width={960}
        destroyOnClose
        onCancel={() => setTrackMapOpen(false)}
      >
        <Spin spinning={trackMapLoading}>
          {trackMapError ? (
            <Alert showIcon type="warning" message={trackMapError} />
          ) : (
            <div ref={trackMapContainerRef} style={{ width: '100%', height: 560 }} />
          )}
        </Spin>
      </Modal>
    </div>
  )
}

export default VideoManagement
